// packages/web/src/components/marketplace/MarketplaceContainer.tsx
// Main marketplace orchestration component

import { Alert, Box, Button, Chip, Container, Grid, Typography } from '@mui/material';
import CssBaseline from '@mui/material/CssBaseline';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import Head from 'next/head';
import { useRouter } from 'next/router';
import React from 'react';

// Components
import { AgentSkillCard } from './AgentSkillCard';
import type { AgentSkill } from './AgentSkillCard';
import { SearchFilters } from './SearchFilters';
import { CategoryNavBar } from './CategoryNavBar';
import { CategoryCardGrid } from './CategoryCardGrid';
import { PromoBar } from './PromoBar';
import { ServiceGrid } from './ServiceGrid';
import ShoppingCart from './ShoppingCart';
// Hooks
import { useAddress } from '../../contexts/AddressContext';
import { useCart } from '../../contexts/CartContext';
import { useAuth } from '../../hooks/useAuth';
import { useAgentSkills } from '../../hooks/useAgentSkills';
import { useCatalog } from '../../hooks/useCatalog';
import { useSearch } from '../../hooks/useSearch';
import { useServiceLoading } from '../../hooks/useServiceLoading';
import type { MarketplaceService } from '../../types/pricing';

interface MarketplaceContainerProps {
  catalogId?: string; // For URL generation (slug)
  catalogInstanceId?: string; // For API calls (UUID)
  initialCatalogData?: { isMainCatalog?: boolean; is_main_catalog?: boolean } | null;
  initialServices?: MarketplaceService[];
  allowPerks?: boolean; // If false, suppress perk chips on all service cards
  branding?: {
    primaryColor?: string;
    secondaryColor?: string;
    backgroundColor?: string;
    backgroundGradient?: string;
    fontFamily?: string;
    logoUrl?: string;
    customCss?: string;
  };
}

/**
 * Main marketplace container component that orchestrates all marketplace functionality
 */
export function MarketplaceContainer({
  catalogId,
  catalogInstanceId,
  initialCatalogData = null,
  initialServices = [],
  allowPerks = true,
  branding,
}: MarketplaceContainerProps) {
  const router = useRouter();
  const { user } = useAuth();

  // Hooks with error handling
  const {
    catalog,
    loading: catalogLoading,
    error: catalogError,
    catalogId: detectedCatalogId,
  } = useCatalog();
  const {
    services,
    loading: servicesLoading,
    error: servicesError,
    hasMore,
    loadMore,
    loadServices,
  } = useServiceLoading(catalogInstanceId, catalogId, initialServices);

  // Safely use cart context (only available for authenticated customers)
  let cartLoading = false;
  let cartOpen = false;
  let closeCart = () => {};
  try {
    const cartContext = useCart();
    cartLoading = cartContext.loading;
    cartOpen = cartContext.cartModalOpen;
    closeCart = cartContext.closeCartModal;
  } catch (error) {
    console.warn(
      '[MarketplaceContainer] CartContext not available, continuing without cart functionality:',
      error,
    );
  }

  // Safely use address context
  let selectedAddress = null;
  try {
    const addressContext = useAddress();
    selectedAddress = addressContext?.selectedAddress || null;
  } catch (error) {
    console.warn(
      '[MarketplaceContainer] AddressContext not available, continuing without address:',
      error,
    );
  }

  const { filters, setQuery, setCategory } = useSearch();

  const [apiCategories, setApiCategories] = React.useState<any[]>([]);
  const [categoriesLoading, setCategoriesLoading] = React.useState(false);

  React.useEffect(() => {
    const fetchCategories = async () => {
      try {
        setCategoriesLoading(true);
        const params = catalogInstanceId ? `?catalogInstanceId=${catalogInstanceId}` : '';
        const response = await fetch(`/api/catalog/categories${params}`);
        if (response.ok) {
          const data = await response.json();
          setApiCategories(Array.isArray(data) ? data : []);
        }
      } catch (e) {
        console.warn('[MarketplaceContainer] Failed to load categories:', e);
      } finally {
        setCategoriesLoading(false);
      }
    };
    fetchCategories();
  }, [catalogInstanceId]);

  // Agent skills (AI marketplace)
  const { skills: agentSkills, loading: skillsLoading } = useAgentSkills();
  const safeAgentSkills = Array.isArray(agentSkills) ? agentSkills : [];

  // Local state
  const [servicesLoaded, setServicesLoaded] = React.useState(false);

  // Handle filter changes - reload services when filters change
  React.useEffect(() => {
    if (catalog && !catalogLoading && servicesLoaded) {
      const activeCatalogIdForAPI = catalogInstanceId || catalogId || detectedCatalogId;
      const addressParam = selectedAddress?.id || undefined;

      const loadOptions: any = {
        address: addressParam,
        query: filters.query,
        category: filters.category,
      };
      if (activeCatalogIdForAPI) {
        loadOptions.catalogInstanceId = activeCatalogIdForAPI;
      }
      if (catalogId) {
        loadOptions.catalogSlug = catalogId;
      }

      console.log('[MARKETPLACE-CONTAINER] Reloading services due to filter change:', loadOptions);
      loadServices(loadOptions);
    }
  }, [
    filters.query,
    filters.category,
    catalog,
    catalogLoading,
    servicesLoaded,
    catalogInstanceId,
    catalogId,
    detectedCatalogId,
    selectedAddress?.id,
  ]);

  // Create catalog-specific theme - use provided branding or catalog branding
  const effectiveBranding = branding || catalog?.branding;
  const theme = React.useMemo(() => {
    const baseTheme = createTheme({
      palette: {
        primary: {
          main: effectiveBranding?.primaryColor || '#f5f5f5',
        },
        secondary: {
          main: effectiveBranding?.secondaryColor || '#e0e0e0',
        },
      },
      typography: {
        fontFamily: effectiveBranding?.fontFamily || '"Roboto", "Helvetica", "Arial", sans-serif',
      },
    });

    return baseTheme;
  }, [effectiveBranding]);

  // Apply branding colors to CSS variables
  React.useEffect(() => {
    if (effectiveBranding) {
      const root = document.documentElement;
      root.style.setProperty(
        '--catalog-primary-color',
        effectiveBranding.primaryColor || '#1976d2',
      );
      root.style.setProperty(
        '--catalog-secondary-color',
        effectiveBranding.secondaryColor || '#dc004e',
      );
      root.style.setProperty(
        '--catalog-background',
        effectiveBranding.backgroundGradient || effectiveBranding.backgroundColor || '#ffffff',
      );
      root.style.setProperty(
        '--catalog-font-family',
        effectiveBranding.fontFamily || '"Roboto", "Helvetica", "Arial", sans-serif',
      );
    }
  }, [effectiveBranding]);

  // Load services when catalog is available
  React.useEffect(() => {
    const activeCatalogIdForAPI = catalogInstanceId || catalogId || detectedCatalogId;
    console.log('[MARKETPLACE-CONTAINER] Catalog state:', {
      catalogId,
      catalogInstanceId,
      detectedCatalogId,
      catalogLoading,
      servicesLoaded,
      activeCatalogIdForAPI,
      catalogExists: !!catalog,
      selectedAddress: selectedAddress?.id,
      userRole: user?.role,
    });

    // For authenticated customers, wait for selectedAddress to be available
    const shouldWaitForAddress = user && user.role === 'customer';
    const addressReady = !shouldWaitForAddress || selectedAddress;

    if (catalog && !catalogLoading && !servicesLoaded && addressReady) {
      console.log('[MARKETPLACE-CONTAINER] Loading services for catalog:', activeCatalogIdForAPI);

      // Pass address if available (IP-detected functionality removed)
      const addressParam = selectedAddress?.id || undefined;

      // Only pass catalogInstanceId if it's a valid catalog instance (not main catalog)
      const loadOptions: any = { address: addressParam };
      if (activeCatalogIdForAPI) {
        loadOptions.catalogInstanceId = activeCatalogIdForAPI;
      }
      if (catalogId) {
        loadOptions.catalogSlug = catalogId;
      }

      loadServices(loadOptions);
      setServicesLoaded(true);
    }
  }, [
    catalogId,
    catalogInstanceId,
    detectedCatalogId,
    catalogLoading,
    servicesLoaded,
    catalog,
    selectedAddress?.id,
    user?.role,
  ]);

  // Reload services when address changes
  React.useEffect(() => {
    const activeCatalogIdForAPI = catalogInstanceId || catalogId || detectedCatalogId;
    if (catalog && !catalogLoading && servicesLoaded && selectedAddress) {
      console.log(
        '[MARKETPLACE-CONTAINER] Reloading services for new address:',
        selectedAddress.id,
      );

      // Pass address if available
      const addressParam = selectedAddress.id || undefined;

      // Only pass catalogInstanceId if it's a valid catalog instance (not main catalog)
      const loadOptions: any = { address: addressParam };
      if (activeCatalogIdForAPI) {
        loadOptions.catalogInstanceId = activeCatalogIdForAPI;
      }
      if (catalogId) {
        loadOptions.catalogSlug = catalogId;
      }

      loadServices(loadOptions);
    }
  }, [catalogId, catalogInstanceId, selectedAddress?.id, catalog, catalogLoading, servicesLoaded]);

  // Handle service click - catalog-aware
  const handleServiceClick = (service: any) => {
    // Only use catalog-aware URLs if catalogId prop is explicitly passed
    // (indicating we're on a catalog page, not the main marketplace)
    if (catalogId) {
      // On catalog page, use catalog-aware URL with slug
      router.push(`/c/${catalogId}/services/${service.id}`);
    } else {
      // On main marketplace, use regular URL
      router.push(`/services/${service.id}`);
    }
  };

  // Get unique categories from actual services returned by the API
  const uniqueCategories = React.useMemo(() => {
    const cats: string[] = ['All', ...Array.from(
      new Set(services.filter((s: any) => s.category).map((s: any) => s.category as string))
    ).sort()];
    if (safeAgentSkills.length > 0) {
      cats.splice(1, 0, 'AI Skills');
    }
    return cats;
  }, [services, safeAgentSkills]);


  const fallbackCategories = React.useMemo(() => {
    const map = new Map<string, { id: string; name: string; slug: string; service_count: number; description?: string }>();
    for (const svc of services as any[]) {
      const name = (svc?.category || '').trim();
      if (!name) continue;
      const slug = name.toLowerCase().replace(/&/g, 'and').replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
      if (!map.has(slug)) {
        map.set(slug, {
          id: slug,
          name,
          slug,
          service_count: 0,
        });
      }
      const row = map.get(slug)!;
      row.service_count += 1;
    }
    return Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [services]);

  const displayCategories = React.useMemo(() => {
    return apiCategories.length > 0 ? apiCategories : fallbackCategories;
  }, [apiCategories, fallbackCategories]);

  const [showSelectOnly, setShowSelectOnly] = React.useState(false);

  // AI Skills subcategory state
  const [skillSubcategory, setSkillSubcategory] = React.useState<string>('All');

  // Get unique AI skill subcategories
  const skillSubcategories = React.useMemo(() => {
    const cats = new Set(safeAgentSkills.map((s) => s.category));
    return ['All', ...Array.from(cats).sort()];
  }, [safeAgentSkills]);

  // Filter agent skills — only show when AI Skills filter is active (hidden by default)
  const filteredSkills = React.useMemo(() => {
    if (filters.category !== 'AI Skills') return [];
    let result = safeAgentSkills;
    if (skillSubcategory !== 'All') {
      result = result.filter((s) => s.category === skillSubcategory);
    }
    if (filters.query) {
      const q = filters.query.toLowerCase();
      result = result.filter(
        (s) =>
          s.name.toLowerCase().includes(q) ||
          s.description.toLowerCase().includes(q) ||
          s.category.toLowerCase().includes(q),
      );
    }
    return result;
  }, [safeAgentSkills, filters.category, filters.query, skillSubcategory]);

  // Handle agent skill click
  const handleSkillClick = (skill: AgentSkill) => {
    router.push(`/agents/skills/${skill.serviceId}`);
  };

  // Should we show IT services? (hide when "AI Skills" filter is active)
  const showItServices = filters.category !== 'AI Skills' && filters.category !== 'All';
  const showAllServices = !filters.category || filters.category === 'All';
  // Combined: show IT services when not in AI Skills-only mode
  const displayItServices = showItServices || showAllServices;

  const catalogAny = (catalog || initialCatalogData) as
    | ((typeof catalog & { is_main_catalog?: boolean }) | { isMainCatalog?: boolean; is_main_catalog?: boolean })
    | null;

  const catalogIsMain =
    catalogAny?.isMainCatalog ??
    catalogAny?.is_main_catalog;

  const isPartnerCatalogView = !!catalogId && catalogIsMain === false;
  // Partner catalogs SSR only ships a thin initial service snapshot. Once the
  // client fetch completes, prefer the hydrated service list so flagship/overlay
  // services and resolved pricing replace the SSR fallback instead of being
  // permanently masked by `initialServices`.
  const sourceServices = isPartnerCatalogView
    ? (services.length > 0 ? services : initialServices)
    : services;

  const visibleServices = React.useMemo(() => {
    const baseServices = allowPerks
      ? sourceServices
      : sourceServices.map((svc: any) => ({ ...svc, winningPerk: null, hasWinningPerk: false }));

    if (!showSelectOnly) return baseServices;
    return baseServices.filter((svc: any) => svc?.is_select === true);
  }, [allowPerks, sourceServices, showSelectOnly]);
  const isHomepageMode = !isPartnerCatalogView && !filters.category && !filters.query;

  const servicesByCategory = React.useMemo(() => {
    const map = new Map<string, any[]>();
    for (const svc of visibleServices as any[]) {
      const cat = (svc?.category || 'Other').trim();
      if (!map.has(cat)) map.set(cat, []);
      map.get(cat)!.push(svc);
    }
    return map;
  }, [visibleServices]);

  const sectionCategories = React.useMemo(() => {
    const fromDisplay = (displayCategories || []).map((c: any) => c?.name).filter(Boolean);
    const fromServices = Array.from(servicesByCategory.keys());
    const merged = Array.from(new Set([...fromDisplay, ...fromServices]));
    return merged.filter((name) => (servicesByCategory.get(name)?.length || 0) > 0);
  }, [displayCategories, servicesByCategory]);


  const catalogName = catalog?.name || 'TechAdds Marketplace';
  const displayError = catalogError || servicesError;
  const isLoading = catalogLoading || servicesLoading || cartLoading;

  // Adjust padding for catalog instances to avoid excessive whitespace
  const containerSx = catalogId
    ? { pt: 0, pb: 2, px: { xs: 1, sm: 2, md: 3, lg: 4 } } // Reduced padding for catalog instances
    : { pt: 1, pb: 4, px: { xs: 2, sm: 3, md: 4, lg: 6 } }; // Standard padding for main marketplace

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Head>
        <title>Marketplace • {catalogName}</title>
      </Head>

      <Container maxWidth={false} sx={containerSx}>
        {/* Error Display (only for IT services, hide when AI Skills filter active) */}
        {displayError && displayItServices && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {displayError}
          </Alert>
        )}

        {/* Category Nav Bar */}
        <CategoryNavBar
          categories={displayCategories}
          activeCategory={filters.category}
          onCategoryChange={(slug) => setCategory(slug)}
          loading={categoriesLoading}
        />

        {/* Search Input (inline, below nav) */}
        <Box sx={{ width: '100%', mb: 2 }}>
          <SearchFilters
            category={filters.category || 'All'}
            query={filters.query}
            onQueryChange={setQuery}
            onCategoryChange={(cat) => setCategory(cat === 'All' ? '' : cat)}
            categories={[]}
            loading={isLoading}
            hideChips={true}
          />
        </Box>

        {/* Promo bar — shown only on homepage */}
        {!filters.category && !filters.query && (
          <PromoBar
            accentColor={effectiveBranding?.primaryColor || '#1976d2'}
            onFilter={(query) => {
              setCategory('');
              setQuery(query);
            }}
          />
        )}

        {/* Category Card Grid — shown only when no category is selected */}
        {!filters.category && !filters.query && (
          <Box id="category-grid">
          <CategoryCardGrid
            onSelectCategory={setCategory}
            accentColor={effectiveBranding?.primaryColor || '#1976d2'}
          />
          </Box>
        )}

        {/* AI Skills Section (only visible when AI Skills filter is active) */}
        {filters.category === 'AI Skills' && (
          <Box sx={{ mb: 4 }}>
            {/* Subcategory filter chips */}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: 2.5 }}>
              {skillSubcategories.map((sub) => (
                <Chip
                  key={sub}
                  label={sub === 'All' ? `All (${safeAgentSkills.length})` : sub}
                  size="small"
                  variant={skillSubcategory === sub ? 'filled' : 'outlined'}
                  color={skillSubcategory === sub ? 'primary' : 'default'}
                  onClick={() => setSkillSubcategory(sub)}
                  sx={{ fontWeight: skillSubcategory === sub ? 700 : 400, cursor: 'pointer' }}
                />
              ))}
            </Box>

            {/* Skills count */}
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {filteredSkills.length} AI skill{filteredSkills.length !== 1 ? 's' : ''} available
              {skillSubcategory !== 'All' ? ` in ${skillSubcategory}` : ''}
            </Typography>

            {/* Skills grid */}
            <Grid container spacing={2}>
              {filteredSkills.map((skill) => (
                <Grid item xs={12} sm={6} md={3} lg={3} key={skill.serviceId}>
                  <AgentSkillCard skill={skill} onCardClick={handleSkillClick} />
                </Grid>
              ))}
            </Grid>
          </Box>
        )}

        {displayItServices && (
          <Box sx={{ mb: 1.5, display: 'flex', justifyContent: 'flex-start' }}>
            <Box
              component="button"
              type="button"
              onClick={() => setShowSelectOnly((v) => !v)}
              sx={{
                width: { xs: '100%', sm: 'auto' },
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 0.75,
                px: 2,
                py: 0.75,
                borderRadius: '999px',
                cursor: 'pointer',
                border: '1.5px solid #D4AF37',
                fontSize: '0.8rem',
                fontWeight: 700,
                letterSpacing: '0.03em',
                transition: 'all 0.2s',
                bgcolor: showSelectOnly ? '#D4AF37' : 'transparent',
                color: showSelectOnly ? '#1a1200' : '#D4AF37',
                '&:hover': { bgcolor: showSelectOnly ? '#c9a830' : 'rgba(212,175,55,0.08)' },
              }}
            >
              <svg width="12" height="12" viewBox="0 0 24 24" aria-hidden="true">
                <path d="M12 2.5L14.95 8.48L21.55 9.44L16.78 14.08L17.9 20.64L12 17.54L6.1 20.64L7.22 14.08L2.45 9.44L9.05 8.48L12 2.5Z" fill="currentColor"/>
              </svg>
              Aithon Select
            </Box>
          </Box>
        )}

        {/* IT Service Grid */}
        {displayItServices && !isHomepageMode && (
          <ServiceGrid
            services={visibleServices}
            loading={isLoading}
            error={displayError}
            hasMore={hasMore}
            onLoadMore={loadMore}
            loadingMore={false} // TODO: Add loading more state
            selectedAddress={selectedAddress}
            onServiceClick={handleServiceClick}
          />
        )}

        {/* Phase 2: Section-based homepage rails */}
        {displayItServices && isHomepageMode && (
          <Box>
            {sectionCategories.map((categoryName) => {
              const categoryServices = (servicesByCategory.get(categoryName) || []).slice(0, 8);
              if (categoryServices.length === 0) return null;

              return (
                <Box key={categoryName} sx={{ mb: 4 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.25 }}>
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>
                      {categoryName}
                    </Typography>
                    <Button
                      size="small"
                      onClick={() => setCategory(categoryName)}
                      sx={{ textTransform: 'none', fontWeight: 600 }}
                    >
                      View all
                    </Button>
                  </Box>

                  <ServiceGrid
                    services={categoryServices}
                    loading={false}
                    error={null}
                    hasMore={false}
                    onLoadMore={async () => {}}
                    loadingMore={false}
                    selectedAddress={selectedAddress}
                    onServiceClick={handleServiceClick}
                  />
                </Box>
              );
            })}
          </Box>
        )}

        {/* Shopping Cart */}
        <ShoppingCart
          open={cartOpen}
          onClose={closeCart}
          onCheckout={() => {
            closeCart();
            // TODO: Implement checkout flow
          }}
        />
      </Container>
    </ThemeProvider>
  );
}
